import { useState, useEffect } from 'react';
import { Checkpoint } from '../../types/tutor';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Lightbulb, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { LatexText } from '../../components/ui/LatexText';

interface InteractiveCheckpointProps {
  checkpoint: Checkpoint;
  stepId: string;
}

export function InteractiveCheckpoint({ checkpoint, stepId }: InteractiveCheckpointProps) {
  const [showHint, setShowHint] = useState<number>(0); // 0: none, 1: hint1, 2: hint2, 3: explanation
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);

  // Reset state when step changes
  useEffect(() => {
    setShowHint(0);
    setSelectedOption(null);
    setFeedback(null);
  }, [stepId]);

  const handleOptionClick = (idx: number) => {
    if (feedback === 'correct') return;
    
    setSelectedOption(idx);
    
    if (checkpoint.type === 'mcq') {
      const isCorrect = idx === checkpoint.answer_index;
      setFeedback(isCorrect ? 'correct' : 'incorrect');
      
      if (!isCorrect) {
        // Show hint if wrong
        if (showHint === 0) setShowHint(1);
      }
    }
  };

  return (
    <div className="space-y-3 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-2 text-blue-700 font-medium">
        <Lightbulb className="w-5 h-5" />
        <span>互动提问</span>
      </div>
      
      <Card className="border-blue-200 bg-blue-50/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 leading-snug">
            <LatexText>{checkpoint.question}</LatexText>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          {checkpoint.type === 'mcq' && checkpoint.options.map((opt, idx) => {
            const isSelected = selectedOption === idx;
            const isCorrect = feedback === 'correct' && isSelected;
            const isWrong = feedback === 'incorrect' && isSelected;
            
            return (
              <Button 
                key={idx} 
                variant="outline" 
                className={cn(
                  "w-full justify-start text-left h-auto py-3 px-4 border-blue-200 text-slate-700 whitespace-normal transition-all",
                  !feedback && "hover:bg-blue-100 hover:text-blue-800",
                  isCorrect && "border-green-500 bg-green-50 text-green-900 ring-1 ring-green-500",
                  isWrong && "border-red-300 bg-red-50 text-red-900",
                  feedback === 'correct' && !isSelected && "opacity-50"
                )}
                onClick={() => handleOptionClick(idx)}
                disabled={feedback === 'correct'}
              >
                <span className={cn(
                  "w-6 h-6 rounded-full border border-current flex items-center justify-center mr-3 shrink-0 text-xs font-bold transition-colors",
                  isCorrect && "bg-green-500 text-white border-green-500",
                  isWrong && "bg-red-500 text-white border-red-500",
                  !isCorrect && !isWrong && "border-slate-400 text-slate-500"
                )}>
                  {isCorrect ? <CheckCircle2 className="w-4 h-4" /> : 
                   isWrong ? <XCircle className="w-4 h-4" /> : 
                   String.fromCharCode(65 + idx)}
                </span>
                <LatexText inline>{opt}</LatexText>
              </Button>
            );
          })}
        </CardContent>
        {feedback && (
          <div className={cn(
            "mx-6 mb-4 p-3 rounded-lg text-sm font-medium flex items-center gap-2 animate-in fade-in slide-in-from-top-2",
            feedback === 'correct' ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          )}>
            {feedback === 'correct' ? (
              <>
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <span>回答正确！请继续下一步。</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span>再试一次，看看下方的提示。</span>
              </>
            )}
          </div>
        )}
        <CardFooter className="pt-2 flex justify-between border-t border-blue-100 mt-2 bg-white/50 rounded-b-lg">
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-blue-600" onClick={() => setShowHint(Math.min(showHint + 1, 3))}>
            <Lightbulb className="w-4 h-4 mr-2" />
            {showHint === 0 ? "我需要提示" : showHint === 1 ? "再给点提示" : "查看解析"}
          </Button>
        </CardFooter>
      </Card>

      {/* Progressive Hints */}
      {showHint >= 1 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 animate-in zoom-in-95">
          <span className="font-bold mr-2">💡 提示 1:</span>
          <LatexText inline>{checkpoint.hint_1}</LatexText>
        </div>
      )}
      {showHint >= 2 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 animate-in zoom-in-95">
          <span className="font-bold mr-2">💡 提示 2:</span>
          <LatexText inline>{checkpoint.hint_2}</LatexText>
        </div>
      )}
      {showHint >= 3 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 animate-in zoom-in-95">
          <span className="font-bold mr-2">📘 解析:</span>
          <LatexText inline>{checkpoint.explanation}</LatexText>
        </div>
      )}
    </div>
  );
}
